import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import RitualCategoryCard from '../components/RitualCategoryCard';
import ProductCard from '../components/ProductCard';
import JournalArticleCard from '../components/JournalArticleCard';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, articlesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`),
        axios.get(`${BACKEND_URL}/api/journal?limit=3`)
      ]);
      setProducts(productsRes.data.slice(0, 3));
      setArticles(articlesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const categories = [
    { name: 'Sleep', key: 'sleep', description: t('categories.sleepDesc') },
    { name: 'Desire', key: 'desire', description: t('categories.desireDesc') },
    { name: 'Focus', key: 'focus', description: t('categories.focusDesc') },
    { name: 'Calm', key: 'calm', description: t('categories.calmDesc') },
    { name: 'Gut', key: 'gut', description: t('categories.gutDesc') },
    { name: 'Breath', key: 'breath', description: t('categories.breathDesc') },
    { name: 'Skin', key: 'skin', description: t('categories.skinDesc') },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(247, 241, 232, 0.7), rgba(226, 199, 164, 0.8)), url('https://images.unsplash.com/photo-1729731322165-241913981ba1')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 py-20 text-center space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <p className="overline text-tiger-orange" data-testid="hero-eyebrow">
              {t('hero.eyebrow')}
            </p>
            <h1 className="heading-hero text-dark-cedar" data-testid="hero-title">
              {t('hero.title')}
            </h1>
            <p className="body-text text-dark-cedar/80 max-w-2xl mx-auto" data-testid="hero-subtitle">
              {t('hero.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              onClick={() => navigate('/ritual')}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              {t('hero.ctaPrimary')}
            </Button>
            <Button
              onClick={() => navigate('/shop')}
              className="btn-ghost"
              data-testid="hero-cta-secondary"
            >
              {t('hero.ctaSecondary')}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-soft-ivory">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="heading-2 text-center mb-16"
          >
            {t('howItWorks.title')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { title: t('howItWorks.step1Title'), text: t('howItWorks.step1Text') },
              { title: t('howItWorks.step2Title'), text: t('howItWorks.step2Text') },
              { title: t('howItWorks.step3Title'), text: t('howItWorks.step3Text') },
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="text-center space-y-4"
              >
                <div className="w-16 h-16 mx-auto bg-tiger-orange/10 rounded-full flex items-center justify-center">
                  <span className="font-playfair text-2xl font-bold text-tiger-orange">
                    {index + 1}
                  </span>
                </div>
                <h3 className="heading-3">{step.title}</h3>
                <p className="text-dark-cedar/70 leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ritual Categories */}
      <section className="section-padding bg-desert-sand/30">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="heading-2 text-center mb-16"
          >
            {t('categories.title')}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <RitualCategoryCard
                  category={category.name}
                  description={category.description}
                  onClick={() => navigate('/ritual', { state: { preselectedCategory: category.name } })}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {products.length > 0 && (
        <section className="section-padding bg-soft-ivory">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="heading-2 mb-4">Featured Rituals</h2>
              <p className="body-text text-dark-cedar/70">Discover our most loved tea blends</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Button
                onClick={() => navigate('/shop')}
                className="btn-secondary"
              >
                View All Products
              </Button>
            </motion.div>
          </div>
        </section>
      )}

      {/* Doctor Note Preview */}
      <section className="section-padding bg-desert-sand">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1646774084704-02e47cc55596"
                alt="Dr. Soomin"
                className="rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="heading-2">Curated by Dr. Soomin</h2>
              <p className="body-text text-dark-cedar/80 leading-relaxed">
                As an aesthetic and wellness physician, I design every tea ritual with your unique body profile and wellness goals in mind. Drawing from both Eastern medicine wisdom and modern science, each blend is crafted to work gently with your natural rhythms.
              </p>
              <Button onClick={() => navigate('/about')} className="btn-ghost">
                Learn More About Our Approach
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Journal Preview */}
      {articles.length > 0 && (
        <section className="section-padding bg-soft-ivory">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="heading-2 mb-4">Journal</h2>
              <p className="body-text text-dark-cedar/70">Insights on wellness, tea, and rituals</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article, index) => (
                <JournalArticleCard key={article.id} article={article} index={index} />
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Button
                onClick={() => navigate('/journal')}
                className="btn-secondary"
              >
                View All Articles
              </Button>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}